import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SSEEventPayload, SSEState, SSEStreamState } from "@src/types/sse";

const initialStreamState = (): SSEStreamState => ({
  isConnected: false,
  lastEvent: null,
  lastEventAt: null,
  events: {},
  history: [],
  hasError: false,
  disconnectedAt: null,
});

const initialState: SSEState = {
  streams: {},
  globalErrors: [],
};

const sseSlice = createSlice({
  name: "sse",
  initialState,
  reducers: {
    setConnected(
      state,
      action: PayloadAction<{ service: string; status: boolean }>,
    ) {
      const { service, status } = action.payload;

      if (!state.streams[service]) {
        state.streams[service] = initialStreamState();
      }

      const stream = state.streams[service];

      stream.isConnected = status;

      if (status) {
        stream.hasError = false;
        stream.disconnectedAt = null;
      } else if (stream.disconnectedAt === null) {
        stream.disconnectedAt = Date.now();
      }
    },

    setEvent(state, action: PayloadAction<SSEEventPayload>) {
      const { service, eventType, data } = action.payload;

      if (!state.streams[service]) {
        state.streams[service] = initialStreamState();
      }

      const stream = state.streams[service];
      const now = Date.now();

      stream.lastEvent = eventType;
      stream.lastEventAt = now;
      stream.events[eventType] = data;

      stream.history.unshift({
        eventType,
        data,
        timestamp: now,
      });

      if (stream.history.length > 50) {
        stream.history.pop();
      }
    },

    setError(
      state,
      action: PayloadAction<{
        service: string;
        hasError: boolean;
        message: string;
      }>,
    ) {
      const { service, hasError, message } = action.payload;

      if (!state.streams[service]) {
        state.streams[service] = initialStreamState();
      }

      const stream = state.streams[service];

      stream.hasError = hasError;

      if (hasError) {
        stream.isConnected = false;
        if (stream.disconnectedAt === null) {
          stream.disconnectedAt = Date.now();
        }
        const existing = state.globalErrors.findIndex(
          (err) => err.service === service,
        );

        if (existing !== -1) {
          state.globalErrors[existing] = {
            service,
            timestamp: Date.now(),
            message,
          };
        } else {
          state.globalErrors.push({ service, timestamp: Date.now(), message });
        }
      } else {
        state.globalErrors = state.globalErrors.filter(
          (err) => err.service !== service,
        );
      }
    },
    clearStream(state, action: PayloadAction<{ service: string }>) {
      const { service } = action.payload;

      if (state.streams[service]) {
        state.streams[service] = initialStreamState();
      }
    },

    clearGlobalErrors(state) {
      state.globalErrors = [];
    },
  },
});

export const {
  setConnected,
  setEvent,
  setError,
  clearStream,
  clearGlobalErrors,
} = sseSlice.actions;

export default sseSlice.reducer;
